import { type SbBlokData, storyblokEditable } from "@storyblok/react/rsc";
import { RichText } from "~components/cms/rich-text";
import { Icon, Rails } from "~components/ui";
import { getLinkProps } from "~lib/storyblok";
import { cn } from "~utils";
import type { TileItemProps } from "../types";
import { TileSectionImage } from "./tile-section-image";

function getTileVariantFlags(variant: string) {
  return {
    isWide: variant === "wide",
    isDefault: variant === "default",
    isSmall: variant === "small",
  };
}

function TileSectionItem({
  blok,
  index,
  isNumbered,
  mainBlok,
  ...context
}: TileItemProps) {
  const titleFontStyle = mainBlok.fontStyleTitleItems ?? "t-prosi-2xl";
  const descriptionFontStyle =
    mainBlok.fontStyleDescriptionItems ?? "t-default-md";
  const { isWide, isSmall } = getTileVariantFlags(mainBlok.variant);

  const textPadding = isSmall ? "py-md md:py-lg" : "p-md md:p-lg";
  const itemSize = isSmall ? "md:w-[min(24em,260px)]" : "md:w-[min(24em,38vw)]";
  const itemBorderRadius = mainBlok.itemBorderRadius ?? "rounded-md";

  return (
    <Rails.Item
      className={cn(
        blok.theme,
        "w-[min(90vw,17em)] shrink-0 snap-center md:snap-start",
        "overflow-hidden md:min-h-[23em]",
        blok.variant === "lighter" && "bg-surface/100",
        blok.variant === "default" && "bg-surface/200",
        blok.variant === "darker" && "bg-surface/300",
        blok.variant === "accent" && "bg-surface/accent",
        isWide && "lg:w-[calc(50%-theme(spacing.lg)/2)]",
        itemSize,
        itemBorderRadius,
      )}
      {...storyblokEditable(blok as SbBlokData)}
    >
      <TileSectionImage blok={blok} mainBlok={mainBlok} />

      <div
        className={cn(
          "grid place-content-start gap-xs text-content-text",
          textPadding,
        )}
      >
        {blok.icon && !isNumbered && (
          <Icon name={blok.icon} weight="duotone" className="circle-icon" />
        )}

        {isNumbered && (
          <div className="t-strong-md box-content grid w-[1em] place-items-center place-self-start rounded-full border border-content-head p-xs leading-none">
            {index + 1}
          </div>
        )}

        {blok.leading && <span className="t-strong-md">{blok.leading}</span>}

        <h2 className={cn("text-balance", titleFontStyle)}>{blok.title}</h2>

        <RichText
          className={cn("text-pretty", descriptionFontStyle)}
          data={blok.text}
          {...context}
        />

        {blok.ctaLink && blok.ctaText && (
          <a
            {...getLinkProps(blok.ctaLink, context)}
            className="t-strong-sm my-2xs leading-tight underline"
          >
            {blok.ctaText}
          </a>
        )}
      </div>
    </Rails.Item>
  );
}

export { TileSectionItem };
